
import { db } from '../../firebase.js';
import { getDoc, setDoc, deleteDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";

class UserService {

  private dbName = process.env.DB === 'TEST' ? 'users_test' : 'users';

  /* USER DATA FUNCTIONS */

  // get user data from firestore database using uid
  async getUserData(uid: string) {
    try {
      const docRef = doc(db, this.dbName, uid);
      const docSnap = await getDoc(docRef);
      return docSnap.data();
    } catch (error) {
      console.log('Error retrieving user data:', error);
    }
  }

  // Generate passport number PC-YYYY-XXXXX
  private generatePassportNumber(): string {
    const year = new Date().getFullYear();
    const rand = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `PC-${year}-${rand}`;
  }

  // Generate referral code from uid
  generateReferralCode(uid: string): string {
    return uid.substring(0, 8).toUpperCase();
  }

  // Create/update user data in firestore — merge: true preserves passport fields
  async setUserData(uid: string, displayName: string, photoURL: string, email: string) {
    try {
      const userRef = doc(db, this.dbName, uid);
      const snap = await getDoc(userRef);
      const isNew = !snap.exists();

      const baseData: any = {
        display_name: displayName,
        avatar_url: photoURL,
        email: email,
      };

      if (isNew) {
        // First time — set all passport fields
        baseData.alias = displayName;
        baseData.passport_number = this.generatePassportNumber();
        baseData.referral_code = this.generateReferralCode(uid);
        baseData.tier = 'dealer';
        baseData.verified = false;
        baseData.kyc_complete = false;
        baseData.created_at = serverTimestamp();
        baseData.referred_by = null;
        baseData.is_td = false;
      }

      await setDoc(userRef, baseData, { merge: true });
      console.log('User data updated successfully.');
    } catch (error) {
      console.error('Error updating user data:', error);
      throw error;
    }
  }

  // Update alias only
  async updateAlias(uid: string, alias: string): Promise<void> {
    try {
      const userRef = doc(db, this.dbName, uid);
      await updateDoc(userRef, { alias });
    } catch (error) {
      console.error('Error updating alias:', error);
      throw error;
    }
  }

  // Find referrer UID by referral code
  async findReferrerByCode(referralCode: string): Promise<string | null> {
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const q = query(collection(db, this.dbName), where('referral_code', '==', referralCode));
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].id;
      return null;
    } catch (error) {
      console.error('Error finding referrer:', error);
      return null;
    }
  }

  // Process referral — record referrer, award bonus tokens to new user
  async processReferral(newUserUid: string, referralCode: string): Promise<void> {
    try {
      const userRef = doc(db, this.dbName, newUserUid);
      const userSnap = await getDoc(userRef);
      // Only process once
      if (userSnap.data()?.referred_by) return;

      const referrerUid = await this.findReferrerByCode(referralCode);
      if (!referrerUid || referrerUid === newUserUid) return;

      // Record referrer on new user
      await updateDoc(userRef, { referred_by: referrerUid });

      // Award 10 bonus tokens to new user
      const { collection, serverTimestamp, runTransaction } = await import('firebase/firestore');
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        const current = snap.data()?.token_balance ?? 0;
        const newBalance = current + 10;
        transaction.update(userRef, { token_balance: newBalance });
        const txRef = doc(collection(db, this.dbName, newUserUid, 'token_transactions'));
        transaction.set(txRef, {
          type: 'player_invited',
          amount: 10,
          balance_after: newBalance,
          description: 'Referral bonus — joined via invite',
          created_at: serverTimestamp()
        });
      });

      console.log('Referral processed successfully');
    } catch (error) {
      console.error('Error processing referral:', error);
    }
  }

  // Record referral on signup
  async setReferredBy(uid: string, referrerUid: string): Promise<void> {
    try {
      const userRef = doc(db, this.dbName, uid);
      await updateDoc(userRef, { referred_by: referrerUid });
    } catch (error) {
      console.error('Error setting referral:', error);
    }
  }

  // Delete user data from firestore database users table
  async deleteUserData(uid: string) {
    try {
      await deleteDoc(doc(db, this.dbName, uid));
      console.log('User data deleted successfully.');
    } catch (error) {
      console.error('Error deleting user data:', error);
      throw error;
    }
  }
}

export default UserService;